import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../AppContextProvider";
import spatialService from "../services/spatialService";
import dealsService from "../services/dealsService";
import { normalizeCounty } from "../utils/trsAggregation";
import { OCC_KINDS, OCC_STATE, splitUnitsByKind } from "../utils/occUnits";
import { sessionCache } from "../utils/sessionCache";
import { readPersisted, writePersisted } from "../utils/persistentCache";

const EMPTY_FC = { type: "FeatureCollection", features: [] };

// `${oid}|${state}|${county}|${kind}` -> FeatureCollection, for the life of
// the tab (utils/sessionCache), so reopening a map refetches nothing.
const cache = sessionCache();

// OCC activity is loaded by a daily ETL only, so the browser also keeps each
// county's units between visits (utils/persistentCache). 12h serves a
// working day from the browser and shows a new load within 12h; de-hub keeps
// its copy for the same time.
export const OCC_KEEP_MS = 12 * 60 * 60 * 1000;

/** The FeatureCollection out of an OCC endpoint response (any envelope). */
export const pickFC = (resp) => {
  const body = resp?.data;
  if (body && body.type === "FeatureCollection") return body;
  if (body?.data && body.data.type === "FeatureCollection") return body.data;
  if (Array.isArray(body?.data?.features)) {
    return { type: "FeatureCollection", features: body.data.features };
  }
  return EMPTY_FC;
};

/**
 * Oklahoma counties only (the OCC endpoints are OK-only), normalised,
 * de-duplicated, sorted, comma-joined — a stable effect key.
 *
 * @param {Array<{ state: string, county: string }>} counties
 * @returns {string}
 */
export const occCountyKey = (counties) =>
  [
    ...new Set(
      (counties || [])
        .filter((c) => String(c?.state ?? "").trim().toUpperCase() === OCC_STATE)
        .map((c) => normalizeCounty(c.county))
        .filter(Boolean)
    ),
  ]
    .sort()
    .join(",");

/**
 * OCC unit overlays for a SET of counties, merged per kind.
 *
 * Only Oklahoma counties are requested (the endpoint is OK-only), one
 * request per county, in parallel; each asks only for the kinds not yet
 * cached for that county. Cache lives as long as the tab, keyed by (user,
 * county, kind), so toggling kinds, narrowing the county set or reopening the
 * map never refetches; and the browser keeps it for OCC_KEEP_MS, so the next
 * visit fetches nothing either (a 404 is not kept). Nothing is fetched with no
 * kind requested.
 *
 * Also loads the `occ_units` field metadata (popup labels) the first time
 * anything is requested.
 *
 * @param {{ counties?: Array<{ state: string, county: string }>,
 *   kinds?: string[] }} args
 * @returns {{ unitsByKind: Record<string, object>, metadata: Array<object>,
 *   loading: boolean, errorMsg: string|null }}
 */
const useOccUnits = ({ counties = [], kinds } = {}) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const oid = user?.idTokenClaims?.oid;

  const [unitsByKind, setUnitsByKind] = useState({});
  const [metadata, setMetadata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const metaRequestedRef = useRef(false);

  // Stable, de-duplicated, display-ordered list of the requested kinds.
  const kindsKey = useMemo(() => {
    const want = new Set(Array.isArray(kinds) ? kinds : []);
    return OCC_KINDS.filter((k) => want.has(k)).join(",");
  }, [kinds]);
  const countyKey = useMemo(() => occCountyKey(counties), [counties]);
  const enabled = !!countyKey && !!kindsKey;

  const resolveToken = async () => {
    let t = token;
    if (!t || (isTokenExpired && isTokenExpired(t))) t = await getToken();
    return t;
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!enabled) {
        setUnitsByKind({});
        setErrorMsg(null);
        return;
      }
      if (!oid) return;
      const wanted = kindsKey.split(",");
      const countyList = countyKey.split(",");
      const keyOf = (county, k) => `${oid}|${OCC_STATE}|${county}|${k}`;
      try {
        // Copies kept from an earlier visit fill the tab cache first.
        await Promise.all(
          countyList.flatMap((county) =>
            wanted
              .filter((k) => !cache.has(keyOf(county, k)))
              .map(async (k) => {
                const kept = await readPersisted(keyOf(county, k));
                if (kept) cache.set(keyOf(county, k), kept);
              })
          )
        );
        const jobs = countyList
          .map((county) => ({
            county,
            missing: wanted.filter((k) => !cache.has(keyOf(county, k))),
          }))
          .filter((j) => j.missing.length > 0);
        if (jobs.length > 0) {
          setLoading(true);
          setErrorMsg(null);
          const t = await resolveToken();
          await Promise.all(
            jobs.map(async ({ county, missing }) => {
              try {
                const resp = await spatialService.getOccUnits(t, oid, {
                  state: OCC_STATE,
                  county,
                  kinds: missing,
                });
                const split = splitUnitsByKind(pickFC(resp), missing);
                for (const k of missing) {
                  cache.set(keyOf(county, k), split[k]);
                  writePersisted(keyOf(county, k), split[k], OCC_KEEP_MS);
                }
              } catch (err) {
                // 404 = nothing for this county; cache as empty.
                if (err?.response?.status !== 404) throw err;
                for (const k of missing) cache.set(keyOf(county, k), EMPTY_FC);
              }
            })
          );
        }
        if (!cancelled) {
          const next = {};
          for (const k of wanted) {
            next[k] = {
              type: "FeatureCollection",
              features: countyList.flatMap(
                (c) => (cache.get(keyOf(c, k)) ?? EMPTY_FC).features
              ),
            };
          }
          setUnitsByKind(next);
        }
      } catch (err) {
        if (!cancelled) setErrorMsg(err?.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, countyKey, kindsKey, oid]);

  // Popup metadata — once, the first time an overlay is requested. A failure
  // leaves the popup with title-cased field names; it never blocks the map.
  useEffect(() => {
    if (!enabled || !oid || metaRequestedRef.current) return;
    metaRequestedRef.current = true;
    (async () => {
      try {
        const t = await resolveToken();
        const resp = await dealsService.getMetadata({
          token: t,
          userOid: oid,
          tableName: "occ_units",
        });
        const rows = resp?.data?.data;
        setMetadata(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.warn("[useOccUnits] occ_units metadata unavailable:", err?.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, oid]);

  return { unitsByKind, metadata, loading, errorMsg };
};

export default useOccUnits;
